// CheckBoxDropdown
// ***********************************************************************************************************************
import { Checkbox, Divider, ListItemText, MenuItem, Select, SelectChangeEvent } from "@mui/material";
import * as React from "react";
import { Base } from "../../../../framework/base";

export interface ICheckboxDropdownItem {
    classes?: string;
    disabled?: boolean;
    id: string;
    getTitle(): string;
}

export interface ICheckboxDropdownProp {
    autoFocus?: boolean;
    classes?: string;
    buttonClasses?: string;
    disabled?: boolean;
    menuRight?: boolean;
    required?: boolean;
    selectedIds: string[];
    selectSubSectionValuesByTitle?: boolean;
    toggleAll?: boolean;
    toggleAllId?: string;
    toggleSingleSelectIds?: string[];
    values: ICheckboxDropdownItem[];
    onChange: (selectedIds: string[]) => void;
    onClose?: () => void;
}

export interface ICheckboxDropdownState {
    selectedValueTitles: string;
}

export class CheckboxDropdown extends React.Component<ICheckboxDropdownProp, ICheckboxDropdownState> {
    private dividerId = "00000000-0000-0000-0000-000000009999";

    dropdownId: string;

    constructor(props: ICheckboxDropdownProp) {
        super(props);
        this.dropdownId = Base.getGuid();
    }

    getToggleAllId = (): string => {
        return this.props.toggleAllId ?? "";
    };

    getToggleSingleSelectIds = (): string[] => {
        return this.props.toggleSingleSelectIds ?? ["00000000-0000-0000-0000-000000001111"];
    };

    getSubSectionItemIdsByItemTitle = (id: string): string[] => {
        if (!id) return [];
        const props = this.props;
        const index = props.values.findIndex(i => i.id === id);
        if (index < 0) return [];
        for (let i = index; i < index; i++) {
            if (props.values[i].id === this.dividerId) return [];
        }
        //Is main section item
        const title = props.values[index].getTitle();
        const result: string[] = [];
        for (let i = index + 1; i < props.values.length; i++) {
            if (props.values[i].getTitle() !== title) continue;
            result.push(props.values[i].id);
        };
        return result;
    };

    getToggleItemIds = (selectedIds: string[], id: string, add: boolean, isSubSectionValueSelect: boolean = false): string[] => {
        const props = this.props;
        const allId = this.getToggleAllId();
        const singleSelectIds = this.getToggleSingleSelectIds();
        let result = selectedIds;
        //Handle add/remove item
        if (props.toggleAll) {
            if (id === allId) {
                if (add) {
                    return [allId];
                } else if (props.values.length > 1) {
                    const newId = props.values.find(i => i.id !== allId);
                    return newId ? [newId.id] : [];
                }
            } else if (singleSelectIds.includes(id)) {
                if (add) {
                    return [id];
                } else if (props.values.length > 1) {
                    const newId = props.values.find(i => !singleSelectIds.includes(i.id));
                    return newId ? [newId.id] : [];
                }
            } else {
                if (add) {
                    result = selectedIds.filter(i => i !== allId && !singleSelectIds.includes(i));
                    if (result.indexOf(id) < 0) {
                        result.push(id);
                    }
                } else {
                    result = result.filter(i => i !== id);
                }
            }
            if (result.length < 1) {
                result.push(allId);
            }
        } else {
            result = selectedIds.filter(i => i !== id);
            if (add) {
                result.push(id);
            }
        }
        //Handle sub section items
        if (props.selectSubSectionValuesByTitle && !isSubSectionValueSelect && id !== allId && !singleSelectIds.includes(id)) {
            const subSectionItemIds = this.getSubSectionItemIdsByItemTitle(id);
            //console.log("subSectionItemIds", subSectionItemIds, id, selectedIds, result);
            for (const subSectionItemId of subSectionItemIds) {
                const subSectionResult = this.getToggleItemIds(result, subSectionItemId, add, true);
                if (!subSectionResult) continue;
                result = subSectionResult;
            }
        }
        return result;
    };

    handleChange = (event: SelectChangeEvent<string[]>) => {
        const props = this.props;
        const {
            target: { value },
        } = event;
        // Figure out if we are adding or removing an item
        let id = null;
        let add = false;
        if (Base.isString(value)) {
            add = true;
            id = value;
        } else if (Array.isArray(value)) {
            add = value.length > props.selectedIds.length;
            if (add) {
                id = value[value.length - 1];
            } else {
                for (const selectedId of props.selectedIds) {
                    if (value.indexOf(selectedId) < 0) {
                        id = selectedId;
                        break;
                    }
                }
            }
        }
        if (Base.isNullOrUndefined(id)) return;
        // Get new selected ids
        const activeItemIds = this.getToggleItemIds(props.selectedIds, id, add);
        if (!activeItemIds) return;
        this.props.onChange(activeItemIds);
    };

    handleClearClick = () => {
        const props = this.props;
        if (props.disabled) return;
        props.onChange([]);
    };

    render() {
        const props = this.props;
        return (
            <div data-id={this.dropdownId} className="input-group checkboxDropdown">
                <Select
                    multiple
                    disabled={props.disabled}
                    autoFocus={props.autoFocus}
                    value={props.selectedIds}
                    onChange={this.handleChange}
                    onClose={props.onClose}
                    renderValue={(selected) => props.values.filter(item => selected.includes(item.id)).map(item => item.getTitle()).join(", ")}
                    size="small"
                    fullWidth
                >
                    {props.values.map(item => {
                        const isDivider = item.id === this.dividerId;
                        if (!isDivider) {
                            return (
                                <MenuItem key={item.id} value={item.id}>
                                    <Checkbox checked={props.selectedIds.indexOf(item.id) > -1} />
                                    <ListItemText primary={item.getTitle()} />
                                </MenuItem>
                            );
                        }
                        const title = item.getTitle();
                        return (
                            <Divider key={item.id + title}>{title}</Divider>
                        );
                    })}
                </Select>
            </div>
        );
    }
}
